import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "../api/client.js";
import {
  categoryLabels,
  useContractor,
  useContractorAction,
  type ContractorRecord,
} from "../api/contractors.js";
import { useOpsMe } from "../api/me.js";

/**
 * Contractor detail + lifecycle actions (ops O3). The approve panel
 * pre-fills from the application the contractor submitted (categories +
 * service-area ZIPs), so onboarding is usually a review-and-confirm. Action
 * buttons show only to admins; the server enforces it regardless.
 */
export function ContractorDetail() {
  const { contractorId } = useParams<{ contractorId: string }>();
  const { data, isPending, isError } = useContractor(contractorId);
  const { data: me } = useOpsMe();
  const isAdmin = (me?.roles ?? []).includes("admin");

  if (isPending) return <p className="text-sm text-charcoal">Loading…</p>;
  if (isError || !data) {
    return (
      <div>
        <BackLink />
        <p className="mt-4 text-sm text-charcoal">Couldn&rsquo;t load this contractor.</p>
      </div>
    );
  }

  const c = data.contractor;
  return (
    <div className="max-w-3xl">
      <BackLink />
      <div className="mt-2 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-charcoal">{c.businessName}</h1>
          <p className="text-sm text-charcoal">
            {c.contactName} · {c.email} · {c.phone}
          </p>
        </div>
        <span className="rounded-full bg-field-gray px-3 py-1 text-xs font-semibold text-charcoal">
          {c.status.replace("_", " ")}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
        <Row label="Services">
          {c.serviceCategories.map((s) => categoryLabels[s] ?? s).join(", ") || "—"}
        </Row>
        <Row label="Service area">
          {c.serviceArea.cities.join(", ") ||
            (c.serviceArea.postalCodes.length > 0
              ? `${c.serviceArea.postalCodes.length} ZIPs`
              : "—")}
        </Row>
        {c.needsTriage && <Row label="Free-text area">{c.needsTriage}</Row>}
        <Row label="Experience">{c.yearsExperience} years</Row>
        {c.website && <Row label="Website">{c.website}</Row>}
        {c.licenseInfo && <Row label="License">{c.licenseInfo}</Row>}
        {c.insuranceInfo && <Row label="Insurance">{c.insuranceInfo}</Row>}
        {c.about && <Row label="About">{c.about}</Row>}
        <Row label="Source">{c.source}</Row>
      </dl>

      {isAdmin && <Actions contractor={c} />}

      <section className="mt-8">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">
          Invitations ({data.invitations.length})
        </h2>
        {data.invitations.length === 0 ? (
          <p className="mt-1 text-sm text-charcoal">None yet.</p>
        ) : (
          <ul className="mt-1 space-y-1 text-sm">
            {data.invitations.map((i) => (
              <li key={i.invitationId} className="text-charcoal">
                {/* The id was plain text, so following a pro back to the job
                    they were asked about meant copying it into the URL. */}
                <Link to={`/customers/${i.rfqId}`} className="font-medium text-brand-700 hover:underline">
                  {i.rfqId}
                </Link>{" "}
                · {i.status} · {new Date(i.createdAt).toLocaleDateString()}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-8">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">History</h2>
        <ul className="mt-1 space-y-1 text-sm">
          {c.audit.map((a, i) => (
            <li key={i} className="text-charcoal">
              <span className="text-charcoal">{new Date(a.at).toLocaleString()}</span> — {a.action}
              {a.by ? ` by ${a.by}` : ""}
              {a.note ? ` (${a.note})` : ""}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Actions({ contractor: c }: { contractor: ContractorRecord }) {
  const action = useContractorAction(c.contractorId);
  const [note, setNote] = useState("");
  const [categories, setCategories] = useState<string[]>(c.serviceCategories);
  const [zips, setZips] = useState(c.serviceArea.postalCodes.join(", "));
  const [error, setError] = useState<string | null>(null);

  const canApprove = c.status === "LEAD" || c.status === "PENDING_REVIEW";
  const canSuspend = c.status === "APPROVED";
  const canReinstate = c.status === "SUSPENDED";

  function run(
    actionName: "approve" | "suspend" | "reinstate",
    body?: Parameters<ReturnType<typeof useContractorAction>["mutate"]>[0]["body"],
  ) {
    setError(null);
    action.mutate(
      { action: actionName, body },
      {
        onError: (err) =>
          setError(
            err instanceof ApiError && err.body && typeof err.body === "object"
              ? ((err.body as { message?: string }).message ?? err.message)
              : err instanceof Error
                ? err.message
                : "Action failed",
          ),
      },
    );
  }

  return (
    <section className="mt-6 rounded-2xl border border-charcoal/15 bg-white p-5">
      <h2 className="text-sm font-semibold text-charcoal">Actions</h2>

      {canApprove && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-charcoal">
            Confirm the services and ZIP codes this pro will match on, then approve.
          </p>
          <fieldset>
            <legend className="mb-1 text-xs font-medium text-charcoal">Services</legend>
            <div className="flex flex-wrap gap-2">
              {Object.entries(categoryLabels).map(([value, label]) => {
                const on = categories.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setCategories((cur) =>
                        on ? cur.filter((x) => x !== value) : [...cur, value],
                      )
                    }
                    className={[
                      "rounded-lg border px-2.5 py-1 text-xs font-medium",
                      on
                        ? "border-brand-700 bg-brand-50 text-brand-800"
                        : "border-charcoal/30 text-charcoal",
                    ].join(" ")}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </fieldset>
          <label className="block">
            <span className="text-xs font-medium text-charcoal">Service-area ZIP codes</span>
            <input
              value={zips}
              onChange={(e) => setZips(e.target.value)}
              placeholder="52801, 52722"
              className="mt-1 w-full rounded-lg border border-charcoal/30 px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            disabled={action.isPending}
            onClick={() => {
              const postalCodes = [
                ...new Set(
                  zips
                    .split(/[,\s]+/)
                    .map((s) => s.trim())
                    .filter(Boolean),
                ),
              ];
              if (categories.length === 0) return setError("Pick at least one service.");
              if (postalCodes.length === 0) return setError("Enter at least one ZIP code.");
              run("approve", {
                serviceCategories: categories,
                serviceArea: { postalCodes, cities: c.serviceArea.cities },
                ...(note.trim() ? { note: note.trim() } : {}),
              });
            }}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {action.isPending ? "Working…" : "Approve contractor"}
          </button>
        </div>
      )}

      {(canSuspend || canReinstate) && (
        <div className="mt-3 flex items-center gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason (optional)"
            className="flex-1 rounded-lg border border-charcoal/30 px-3 py-2 text-sm"
          />
          {canSuspend && (
            <button
              type="button"
              disabled={action.isPending}
              onClick={() => run("suspend", { ...(note.trim() ? { note: note.trim() } : {}) })}
              className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              Suspend
            </button>
          )}
          {canReinstate && (
            <button
              type="button"
              disabled={action.isPending}
              onClick={() => run("reinstate", { ...(note.trim() ? { note: note.trim() } : {}) })}
              className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              Reinstate
            </button>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="font-medium text-charcoal">{label}</dt>
      <dd className="text-charcoal">{children}</dd>
    </>
  );
}

function BackLink() {
  return (
    <Link to="/contractors" className="text-sm font-semibold text-brand-700">
      &larr; Contractors
    </Link>
  );
}
